<template>
  <div>
    <b-card no-body>
      <div class="card-header">
        <b-card-title>{{ $t('settings.governorate.list') }}</b-card-title>
        <div class="d-flex align-items-center">
          <b-button
            v-ripple.400="'rgba(255, 255, 255, 0.15)'"
            variant="primary"
            class="mr-1"
            :to="{ name: 'settings-governorates-add' }"
          >
            <feather-icon
              icon="PlusIcon"
              class="mr-50"
            />
            <span class="align-middle">{{ $t('settings.governorate.add') }}</span>
          </b-button>
          <b-button
            v-ripple.400="'rgba(255, 255, 255, 0.15)'"
            variant="success"
            class="mr-1"
            @click="exportGovernorates"
          >
            <feather-icon
              icon="DownloadIcon"
              class="mr-50"
            />
            <span class="align-middle">{{ $t('common.export') }}</span>
          </b-button>
          <b-button
            v-ripple.400="'rgba(255, 255, 255, 0.15)'"
            variant="success"
            @click="openImportModal"
          >
            <feather-icon
              icon="UploadIcon"
              class="mr-50"
            />
            <span class="align-middle">{{ $t('common.import') }}</span>
          </b-button>
        </div>
      </div>

      <div class="card-body">
        <b-row>
          <b-col
            cols="12"
            md="6"
          >
            <b-form-group>
              <b-form-input
                v-model="filters.search"
                :placeholder="$t('common.search')"
                @input="fetchGovernorates"
              />
            </b-form-group>
          </b-col>
          <b-col
            cols="12"
            md="3"
          >
            <b-form-group>
              <v-select
                v-model="filters.is_active"
                :options="statusOptions"
                :reduce="val => val.value"
                :placeholder="$t('common.status')"
                @input="fetchGovernorates"
              />
            </b-form-group>
          </b-col>
          <b-col
            cols="12"
            md="3"
          >
            <b-button
              variant="outline-secondary"
              @click="clearFilters"
            >
              {{ $t('common.clear') }}
            </b-button>
          </b-col>
        </b-row>

        <b-table
          :items="governorates"
          :fields="fields"
          :busy="loading"
          responsive
          show-empty
          :empty-text="$t('common.no_data')"
        >
          <template #table-busy>
            <div class="text-center text-primary my-2">
              <b-spinner class="align-middle mr-1" />
              <strong>{{ $t('common.loading') }}</strong>
            </div>
          </template>

          <template #cell(is_active)="data">
            <b-badge :variant="data.item.is_active ? 'success' : 'danger'">
              {{ data.item.is_active ? $t('common.active') : $t('common.inactive') }}
            </b-badge>
          </template>

          <template #cell(actions)="data">
            <b-dropdown
              variant="link"
              no-caret
              :right="$store.state.appConfig.isRTL"
            >
              <template #button-content>
                <feather-icon
                  icon="MoreVerticalIcon"
                  size="16"
                  class="align-middle text-body"
                />
              </template>
              <b-dropdown-item :to="{ name: 'settings-governorates-view', params: { id: data.item.id } }">
                <feather-icon icon="EyeIcon" />
                <span class="align-middle ml-50">{{ $t('common.view') }}</span>
              </b-dropdown-item>
              <b-dropdown-item :to="{ name: 'settings-governorates-edit', params: { id: data.item.id } }">
                <feather-icon icon="EditIcon" />
                <span class="align-middle ml-50">{{ $t('common.edit') }}</span>
              </b-dropdown-item>
              <b-dropdown-item @click="toggleStatus(data.item)">
                <feather-icon icon="ToggleLeftIcon" />
                <span class="align-middle ml-50">{{ $t('common.toggle_status') }}</span>
              </b-dropdown-item>
              <b-dropdown-item @click="deleteGovernorate(data.item)">
                <feather-icon icon="TrashIcon" />
                <span class="align-middle ml-50">{{ $t('common.delete') }}</span>
              </b-dropdown-item>
            </b-dropdown>
          </template>
        </b-table>

        <b-pagination
          v-model="currentPage"
          :total-rows="totalRows"
          :per-page="perPage"
          align="center"
          @change="handlePageChange"
        />
      </div>
    </b-card>
    <b-modal
      ref="importModal"
      title="تحميل محافظات"
      ok-title="تحميل"
      cancel-title="إلغاء"
      @ok="importGovernorates"
    >
      <b-form-file
        v-model="importFile"
        :placeholder="$t('common.select_file')"
        @change="handleImportFileChange"
      />

    </b-modal>
  </div>
</template>

<script>
import {
  BCard,
  BCardTitle,
  BButton,
  BRow,
  BCol,
  BFormGroup,
  BFormInput,
  BTable,
  BBadge,
  BDropdown,
  BDropdownItem,
  BSpinner,
  BPagination,
  BModal,
  BFormFile,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import Ripple from 'vue-ripple-directive'
import { mapGetters } from 'vuex'

export default {
  components: {
    BCard,
    BCardTitle,
    BButton,
    BRow,
    BCol,
    BFormGroup,
    BFormInput,
    BTable,
    BBadge,
    BDropdown,
    BDropdownItem,
    BSpinner,
    BPagination,
    vSelect,
    BModal,
    BFormFile,
  },
  directives: {
    Ripple,
  },
  data() {
    return {
      filters: {
        search: '',
        is_active: null,
      },
      currentPage: 1,
      perPage: 15,
      totalRows: 0,
      statusOptions: [
        { label: this.$t('common.all'), value: null },
        { label: this.$t('common.active'), value: true },
        { label: this.$t('common.inactive'), value: false },
      ],
      fields: [
        // { key: 'id', label: 'ID', sortable: true },
        { key: 'name_ar', label: this.$t('settings.governorate.name_ar'), sortable: true },
        { key: 'name_en', label: this.$t('settings.governorate.name_en'), sortable: true },
        { key: 'code', label: this.$t('settings.governorate.code'), sortable: true },
        { key: 'cities_count', label: this.$t('settings.governorate.cities_count'), sortable: true },
        { key: 'is_active', label: this.$t('common.status'), sortable: true },
        { key: 'actions', label: this.$t('common.actions') },
      ],
    }
  },
  computed: {
    ...mapGetters({
      governorates: 'governorates/governorates',
      loading: 'governorates/load',
    }),
  },
  mounted() {
    this.fetchGovernorates()
  },
  methods: {
    fetchGovernorates() {
      const params = {
        ...this.filters,
        page: this.currentPage,
        per_page: this.perPage,
      }
      this.$store.dispatch('governorates/list', params)
        .then(response => {
          if (response.meta) {
            this.totalRows = response.meta.total
          }
        })
        .catch(error => {
          this.$swal('خطأ!', error?.response?.data?.message || 'حدث خطأ أثناء الحذف', 'error')
        })
    },
    clearFilters() {
      this.filters = {
        search: '',
        is_active: null,
      }
      this.fetchGovernorates()
    },
    handlePageChange(page) {
      this.currentPage = page
      this.fetchGovernorates()
    },
    toggleStatus(governorate) {
      this.$swal({
        title: this.$t('common.confirm'),
        text: this.$t('settings.governorate.messages.toggle_status_confirm'),
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: this.$t('common.yes'),
        cancelButtonText: this.$t('common.no'),
        customClass: {
          confirmButton: 'btn btn-primary',
          cancelButton: 'btn btn-outline-danger ml-1',
        },
        buttonsStyling: false,
      }).then(result => {
        if (result.value) {
          this.$store.dispatch('governorates/toggleStatus', governorate.id)
            .then(() => {
              this.$swal('تم التبديل!', 'تم تبديل الحالة.', 'success')
              this.fetchGovernorates()
            })
            .catch(error => {
              this.$swal('خطأ!', error?.response?.data?.message || 'حدث خطأ أثناء التبديل', 'error')
            })
        }
      })
    },
    deleteGovernorate(governorate) {
      this.$swal({
        title: this.$t('common.confirm'),
        text: this.$t('settings.governorate.messages.delete_confirm'),
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: this.$t('common.yes'),
        cancelButtonText: this.$t('common.no'),
        customClass: {
          confirmButton: 'btn btn-primary',
          cancelButton: 'btn btn-outline-danger ml-1',
        },
        buttonsStyling: false,
      }).then(result => {
        if (result.value) {
          this.$store.dispatch('governorates/delete', governorate.id)
            .then(() => {
              this.$swal('تم الحذف!', 'تم حذف المحافظة.', 'success')
              this.fetchGovernorates()
            })
            .catch(error => {
              this.$swal('خطأ!', error?.response?.data?.message || 'حدث خطأ أثناء الحذف', 'error')
            })
        }
      })
    },
    exportGovernorates() {
      this.$store.dispatch('governorates/export', this.filters)
        .then(response => {
          // request interceptor returns response.data (blob) directly, so use it as-is
          const blob = new Blob(
            [response],
            { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' },
          )
          const url = window.URL.createObjectURL(blob)
          const link = document.createElement('a')
          link.href = url
          link.setAttribute('download', `governorates_${new Date().getTime()}.xlsx`)
          document.body.appendChild(link)
          link.click()
          link.remove()
          this.$swal('تم التصدير!', 'تم تصدير المحافظات.', 'success')
        })
        .catch(error => {
          this.$swal('خطأ!', error?.response?.data?.message || 'حدث خطأ أثناء التصدير', 'error')
        })
    },
    openImportModal() {
      this.$refs.importModal.show()
    },
    handleImportFileChange(file) {
      this.importFile = file
    },
    importGovernorates() {
      const formData = new FormData()
      formData.append('file', this.importFile)
      this.$store.dispatch('governorates/import', formData)
        .then(response => {
          this.$swal('تم التحميل!', 'تم تحميل المحافظات.', 'success')
          this.$refs.importModal.hide()
        })
        .catch(error => {
          this.$swal('خطأ!', error?.response?.data?.message || 'حدث خطأ أثناء التحميل', 'error')
        })
    },
  },
}
</script>

<style lang="scss" scoped>
@import '~@core/scss/vue/libs/vue-select.scss';
</style>
